// Script diff on collection merge (SCR-20, ARCH §27.15). Client-only (ENG-1).
//
// When a collection's version of a script would overwrite one the author edited, the
// editor shows a LINE-LEVEL diff and asks: keep mine, take theirs, or merge by hand.
// With live push and pinned collections scripts move between machines routinely, and a
// silent overwrite of an author's edit is the obvious way to lose work.
//
// The top half is PURE — the diff, the three resolutions and the "should we even ask?"
// rule — so every claim about what the author ends up with is testable without a DOM.
// The bottom half is the prompt, and it has nothing to decide: it renders a diff and
// hands the chosen text back.
import { h } from '../h';

/** One line of a diff. `changed` pairs a line of mine with the line of theirs that
 *  replaced it (a removal immediately answered by an addition), which is how an author
 *  reads an edit — "this line became that one" — rather than as two unrelated events. */
export type DiffLine =
  | { readonly kind: 'same'; readonly text: string; readonly mine: number; readonly theirs: number }
  | { readonly kind: 'removed'; readonly text: string; readonly mine: number }
  | { readonly kind: 'added'; readonly text: string; readonly theirs: number }
  | { readonly kind: 'changed'; readonly from: string; readonly to: string; readonly mine: number; readonly theirs: number };

/** A source split into lines — on `\n` or `\r\n`, so a file saved on another machine
 *  does not diff as entirely changed. Line numbers in a `DiffLine` are 1-based. */
function lines(source: string): string[] {
  return source.split(/\r?\n/);
}

/** The line-level diff from `mine` to `theirs` — a longest-common-subsequence walk, with
 *  each run of removals directly followed by a run of additions paired up as `changed`.
 *  Deterministic: ties prefer keeping MY line first, so the same pair always diffs the
 *  same way. Identical sources produce no line that is not `same`. */
export function diffLines(mine: string, theirs: string): DiffLine[] {
  const a = lines(mine);
  const b = lines(theirs);
  // lcs[i][j] = LCS length of a[i..] and b[j..].
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const raw: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      raw.push({ kind: 'same', text: a[i]!, mine: i + 1, theirs: j + 1 });
      i++; j++;
    } else if (j >= b.length || (i < a.length && lcs[i + 1]![j]! >= lcs[i]![j + 1]!)) {
      raw.push({ kind: 'removed', text: a[i]!, mine: i + 1 });
      i++;
    } else {
      raw.push({ kind: 'added', text: b[j]!, theirs: j + 1 });
      j++;
    }
  }
  return pairChanges(raw);
}

/** Pair each run of removals with the run of additions right after it, line for line;
 *  whatever is left over on either side stays a plain removal or addition. */
function pairChanges(raw: readonly DiffLine[]): DiffLine[] {
  const out: DiffLine[] = [];
  for (let k = 0; k < raw.length;) {
    if (raw[k]!.kind !== 'removed') { out.push(raw[k]!); k++; continue; }
    const removed: Extract<DiffLine, { kind: 'removed' }>[] = [];
    while (k < raw.length && raw[k]!.kind === 'removed') removed.push(raw[k++] as Extract<DiffLine, { kind: 'removed' }>);
    const added: Extract<DiffLine, { kind: 'added' }>[] = [];
    while (k < raw.length && raw[k]!.kind === 'added') added.push(raw[k++] as Extract<DiffLine, { kind: 'added' }>);
    const pairs = Math.min(removed.length, added.length);
    for (let p = 0; p < pairs; p++) {
      out.push({ kind: 'changed', from: removed[p]!.text, to: added[p]!.text, mine: removed[p]!.mine, theirs: added[p]!.theirs });
    }
    out.push(...removed.slice(pairs), ...added.slice(pairs));
  }
  return out;
}

/** The three answers §27.15 offers. `by-hand` carries the text the author settled on. */
export type MergeChoice =
  | { readonly kind: 'keep-mine' }
  | { readonly kind: 'take-theirs' }
  | { readonly kind: 'by-hand'; readonly text: string };

/** The source the author ends up with. Keep-mine and take-theirs are EXACT — byte for
 *  byte the side chosen, never a re-joined or re-normalised copy of it. */
export function resolveMerge(mine: string, theirs: string, choice: MergeChoice): string {
  switch (choice.kind) {
    case 'keep-mine': return mine;
    case 'take-theirs': return theirs;
    case 'by-hand': return choice.text;
  }
}

/** The starting text for "merge by hand": every line both sides agree on as-is, and
 *  every disagreement fenced in the conventional markers, so the author edits one buffer
 *  and deletes what they do not want. */
export function mergeDraft(mine: string, theirs: string): string {
  const out: string[] = [];
  let ours: string[] = [];
  let other: string[] = [];
  const flush = (): void => {
    if (ours.length === 0 && other.length === 0) return;
    out.push('<<<<<<< mine', ...ours, '=======', ...other, '>>>>>>> theirs');
    ours = []; other = [];
  };
  for (const d of diffLines(mine, theirs)) {
    if (d.kind === 'same') { flush(); out.push(d.text); continue; }
    if (d.kind === 'changed') { ours.push(d.from); other.push(d.to); continue; }
    if (d.kind === 'removed') ours.push(d.text);
    else other.push(d.text);
  }
  flush();
  return out.join('\n');
}

/** Should the author be asked at all? Only when taking theirs would LOSE something:
 *  `mine` holds text, it differs from `theirs`, and it is not itself a pristine copy of
 *  something the author loaded without editing (`pristine` — e.g. another shipped
 *  example). Identical sources produce no diff and no prompt. */
export function needsMergePrompt(mine: string, theirs: string, pristine: readonly string[] = []): boolean {
  if (mine === theirs) return false;
  if (mine.trim() === '') return false;
  return !pristine.includes(mine);
}

/** Counts for the prompt's summary line. */
export function diffSummary(diff: readonly DiffLine[]): { changed: number; added: number; removed: number } {
  const count = (k: DiffLine['kind']): number => diff.filter((d) => d.kind === k).length;
  return { changed: count('changed'), added: count('added'), removed: count('removed') };
}

// ── The prompt ────────────────────────────────────────────────────────────────

/** Render §27.15's prompt: the diff (mine → theirs) and the three choices. `onResolve`
 *  receives the text to put in the editor — for merge-by-hand, the marked-up draft. */
export function scriptDiffPrompt(opts: {
  title: string;
  mine: string;
  theirs: string;
  onResolve: (text: string, choice: MergeChoice['kind']) => void;
}): HTMLElement {
  const diff = diffLines(opts.mine, opts.theirs);
  const { changed, added, removed } = diffSummary(diff);
  const rows = diff.map((d) => {
    switch (d.kind) {
      case 'same': return h('div', { class: 'bb-diff-line' }, `  ${d.text}`);
      case 'removed': return h('div', { class: 'bb-diff-line bb-diff-removed', 'data-diff': 'removed' }, `- ${d.text}`);
      case 'added': return h('div', { class: 'bb-diff-line bb-diff-added', 'data-diff': 'added' }, `+ ${d.text}`);
      case 'changed': return h('div', { 'data-diff': 'changed' },
        h('div', { class: 'bb-diff-line bb-diff-removed' }, `- ${d.from}`),
        h('div', { class: 'bb-diff-line bb-diff-added' }, `+ ${d.to}`));
    }
  });
  const choose = (kind: MergeChoice['kind']): void => {
    const text = kind === 'by-hand'
      ? mergeDraft(opts.mine, opts.theirs)
      : resolveMerge(opts.mine, opts.theirs, { kind });
    opts.onResolve(text, kind);
  };
  const button = (kind: MergeChoice['kind'], label: string, cls = 'bb-btn'): HTMLElement => {
    const b = h('button', { class: cls, 'data-testid': `diff-${kind}` }, label);
    b.onclick = () => { choose(kind); };
    return b;
  };
  return h('div', { class: 'bb-diff', 'data-testid': 'script-diff' },
    h('div', { class: 'bb-label' }, opts.title),
    h('div', { class: 'bb-diff-summary' },
      `Your edit differs: ${changed} changed, ${added} added, ${removed} removed (− yours, + theirs).`),
    h('div', { class: 'bb-diff-body' }, ...rows),
    h('div', { class: 'bb-script-actions' },
      button('keep-mine', 'Keep mine', 'bb-btn bb-primary'),
      button('take-theirs', 'Take theirs'),
      button('by-hand', 'Merge by hand')),
  );
}
